import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Typography } from '@heroui/react';
import { Icon } from '@iconify/react';
import { getNodeTestIdProp } from '@/utils';
import type { PosProduct } from '../../posProduct';
import { formatGel } from '../../sellerMeta';

type ProductTileProps = {
    product: PosProduct;
    /** Units in the cart across all of the product's variants. */
    inCartQty: number;
};

/**
 * A single product tile's content: an in-cart quantity badge, the stock state
 * as muted figures or a danger chip when out of stock, and — for a
 * several-variant product — its variant count and lowest price. Content only:
 * the interactive tile around it (press, focus, disabled state) is its
 * `ListBox.Item` in `ProductGrid` (SEL-39) — the design system's action API,
 * not a hand-built button. The name reserves two lines so prices align across
 * the row regardless of name length; the price block sticks to the bottom
 * (`mt-auto`) so it lines up even inside the grid's fixed tile height.
 */
export const ProductTile: React.FC<ProductTileProps> = ({
    product,
    inCartQty,
}) => {
    const { t } = useTranslation('pages');
    const outOfStock = product.stock <= 0;
    const variantCount = product.variants.length;

    return (
        <div
            className="relative flex h-full flex-col gap-2 text-left"
            {...getNodeTestIdProp(`seller-product-tile-content-${product.id}`)}
        >
            {inCartQty > 0 ? (
                <span
                    className="absolute right-0 top-0 z-10 flex min-w-5 items-center justify-center rounded-full bg-accent px-1.5 text-xs font-bold tabular-nums text-accent-foreground"
                    {...getNodeTestIdProp(
                        `seller-product-incart-${product.id}`,
                    )}
                >
                    {inCartQty}
                </span>
            ) : null}

            <div className="flex size-9 items-center justify-center rounded-xl bg-surface-secondary text-muted">
                <Icon className="size-[18px]" icon="solar:box-bold-duotone" />
            </div>

            <Typography className="line-clamp-2 min-h-[2.5rem]" type="body-sm">
                {product.name}
            </Typography>

            <div className="mt-auto flex flex-col items-start gap-1 pt-1">
                <Typography
                    className="leading-none tabular-nums text-accent"
                    weight="bold"
                >
                    {formatGel(product.price)}
                </Typography>
                {variantCount > 1 ? (
                    <Typography
                        className="tabular-nums"
                        color="muted"
                        type="body-xs"
                    >
                        {t('seller.variantCount', { count: variantCount })}
                    </Typography>
                ) : null}
                {outOfStock ? (
                    <Chip color="danger" size="sm" variant="soft">
                        {t('seller.outOfStock')}
                    </Chip>
                ) : (
                    <Typography
                        className="tabular-nums"
                        color="muted"
                        type="body-xs"
                    >
                        {t('seller.inStock', { count: product.stock })}
                    </Typography>
                )}
            </div>
        </div>
    );
};
